import { useEffect, useRef } from "react";
import { useUnmount } from "usehooks-ts";
import {
  hasAddedConsolidatedOrderingProduct,
  mapConsolidatedOrderingIntercomSource,
  useConsolidatedOrderingIntercom,
} from "../../../../hooks/useConsolidatedOrderingIntercom";
import type { ConsolidatedOrderingJourneySource } from "../../../../hooks/useMarketStorefrontSessionTracking";
import { DeliveredBySellarFilterOption } from "../DiscoveryV2SearchStore";

const ELIGIBILITY_DELAY_MS = 60_000;
const SEARCH_DEBOUNCE_MS = 500;

type SearchContext = {
  sessionId: string | null;
  journeySource: ConsolidatedOrderingJourneySource;
  isDeliveredBySellar: boolean;
  isLoading: boolean;
  resultCount: number;
  selectedSupplierCount: number;
  deliveryFilter: DeliveredBySellarFilterOption;
  orderCutoffAt?: string;
};

type JourneyState = {
  sessionId: string | null;
  startedAt: number;
  searchCount: number;
  filterChangeCount: number;
  lastSearch: string;
  isTyping: boolean;
};

const createJourneyState = (sessionId: string | null): JourneyState => ({
  sessionId,
  startedAt: Date.now(),
  searchCount: 0,
  filterChangeCount: 0,
  lastSearch: "",
  isTyping: false,
});

export const useConsolidatedOrderingSearchIntercom = (
  context: SearchContext,
) => {
  const { trackEventOnce, updateJourney } = useConsolidatedOrderingIntercom();
  const contextRef = useRef(context);
  useEffect(() => {
    contextRef.current = context;
  }, [context]);
  const journeyRef = useRef(createJourneyState(context.sessionId));
  const searchTimerRef = useRef<number | undefined>(undefined);
  const eligibilityTimerRef = useRef<number | undefined>(undefined);
  const filterTimerRef = useRef<number | undefined>(undefined);
  const filterInteractionPendingRef = useRef(false);

  const getJourney = () => {
    if (journeyRef.current.sessionId !== contextRef.current.sessionId) {
      journeyRef.current = createJourneyState(contextRef.current.sessionId);
    }
    return journeyRef.current;
  };

  const checkEligibility = () => {
    eligibilityTimerRef.current = undefined;
    const current = contextRef.current;
    const journey = getJourney();
    if (!current.isDeliveredBySellar || !current.sessionId) return;
    if (journey.searchCount + journey.filterChangeCount < 2) return;
    if (hasAddedConsolidatedOrderingProduct(current.sessionId)) return;
    if (current.isLoading || journey.isTyping) {
      eligibilityTimerRef.current = window.setTimeout(checkEligibility, 1_000);
      return;
    }

    trackEventOnce(
      `${current.sessionId}:search-feedback-eligible`,
      "consolidated_ordering_search_feedback_eligible",
      {
        sessionId: current.sessionId,
        ...(mapConsolidatedOrderingIntercomSource(current.journeySource)
          ? {
              source: mapConsolidatedOrderingIntercomSource(
                current.journeySource,
              ),
            }
          : {}),
        searchCount: journey.searchCount,
        filterChangeCount: journey.filterChangeCount,
        resultCount: current.resultCount,
        selectedSupplierCount: current.selectedSupplierCount,
        deliveryFilter: current.deliveryFilter,
        ...(current.orderCutoffAt
          ? { orderCutoffAt: current.orderCutoffAt }
          : {}),
      },
    );
  };

  const recordInteraction = () => {
    const current = contextRef.current;
    if (!current.isDeliveredBySellar || !current.sessionId) return;

    updateJourney({
      journeyKey: current.sessionId,
      status: "searching",
      step: "search",
      snapshot: {
        sessionId: current.sessionId,
        journeySource: current.journeySource,
        supplierCount: current.selectedSupplierCount,
        itemCount: 0,
      },
    });

    if (eligibilityTimerRef.current !== undefined) return;
    const delay = Math.max(
      getJourney().startedAt + ELIGIBILITY_DELAY_MS - Date.now(),
      0,
    );
    eligibilityTimerRef.current = window.setTimeout(checkEligibility, delay);
  };

  const onSearchChanged = (searchValue: string) => {
    const journey = getJourney();
    journey.isTyping = true;
    window.clearTimeout(searchTimerRef.current);
    searchTimerRef.current = window.setTimeout(() => {
      journey.isTyping = false;
      const search = searchValue.trim();
      if (!search || search === journey.lastSearch) return;
      journey.lastSearch = search;
      journey.searchCount += 1;
      recordInteraction();
    }, SEARCH_DEBOUNCE_MS);
  };

  const onSearchSubmitted = (searchValue: string) => {
    const journey = getJourney();
    window.clearTimeout(searchTimerRef.current);
    journey.isTyping = false;
    const search = searchValue.trim();
    if (!search || search === journey.lastSearch) return;
    journey.lastSearch = search;
    journey.searchCount += 1;
    recordInteraction();
  };

  const onFilterChanged = () => {
    if (filterInteractionPendingRef.current) return;
    filterInteractionPendingRef.current = true;
    filterTimerRef.current = window.setTimeout(() => {
      filterInteractionPendingRef.current = false;
      getJourney().filterChangeCount += 1;
      recordInteraction();
    }, 0);
  };

  useUnmount(() => {
    window.clearTimeout(searchTimerRef.current);
    window.clearTimeout(eligibilityTimerRef.current);
    window.clearTimeout(filterTimerRef.current);
  });

  return { onSearchChanged, onSearchSubmitted, onFilterChanged };
};
